import { useCallback, useEffect, useRef, useState } from "react";
import jsQR from "jsqr";

type QRScannerProps = {
  onScanSuccess: (decodedText: string) => void | Promise<void>;
  onScanFailure?: (error: string) => void;
  hasScannedError?: boolean;
  setHasScannedError?: (value: boolean) => void;
  showScanner?: boolean;
  restartTrigger?: number;
};

type CameraPermissionState =
  | "checking"
  | "prompt"
  | "requesting"
  | "granted"
  | "denied";

type BarcodeDetectorLike = {
  detect: (
    source: ImageBitmapSource
  ) => Promise<Array<{ rawValue?: string }>>;
};

const SCAN_INTERVAL_MS = 100;

const getBarcodeDetector = (): BarcodeDetectorLike | null => {
  const Detector = (
    window as Window & {
      BarcodeDetector?: new (options?: {
        formats?: string[];
      }) => BarcodeDetectorLike;
    }
  ).BarcodeDetector;
  if (!Detector) return null;
  try {
    return new Detector({ formats: ["qr_code"] });
  } catch {
    return null;
  }
};

const decodeWithJsQR = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): string | null => {
  const imageData = ctx.getImageData(0, 0, width, height);
  const result = jsQR(imageData.data, width, height, {
    inversionAttempts: "attemptBoth",
  });
  return result?.data || null;
};

const QRScanner = ({
  onScanSuccess,
  hasScannedError,
  setHasScannedError,
  showScanner = true,
  restartTrigger = 0,
}: QRScannerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scanLoopRef = useRef<number | null>(null);
  const runIdRef = useRef(0);
  const hasScannedRef = useRef(false);
  const scanBlockedRef = useRef(false);
  const detectingRef = useRef(false);
  const onScanSuccessRef = useRef(onScanSuccess);
  const barcodeDetectorRef = useRef<BarcodeDetectorLike | null>(null);

  const [redLine, setRedLine] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [scanTimedOut, setScanTimedOut] = useState(false);
  const [cameraPermission, setCameraPermission] =
    useState<CameraPermissionState>("checking");
  const [scannerEpoch, setScannerEpoch] = useState(0);

  onScanSuccessRef.current = onScanSuccess;

  const stopIdleTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    stopIdleTimer();
    timerRef.current = setInterval(() => {
      if (!hasScannedRef.current) {
        setScanTimedOut(true);
      }
    }, 20000);
  }, [stopIdleTimer]);

  const stopStream = useCallback(() => {
    if (scanLoopRef.current != null) {
      window.clearTimeout(scanLoopRef.current);
      scanLoopRef.current = null;
    }
    stopIdleTimer();
    detectingRef.current = false;

    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    const video = videoRef.current;
    if (video) {
      video.srcObject = null;
    }
  }, [stopIdleTimer]);

  const handleDecoded = useCallback(
    (decodedText: string) => {
      if (hasScannedRef.current || scanBlockedRef.current) return;
      if (!decodedText?.trim()) return;

      hasScannedRef.current = true;
      setRedLine(false);
      stopIdleTimer();
      if (scanLoopRef.current != null) {
        window.clearTimeout(scanLoopRef.current);
        scanLoopRef.current = null;
      }
      onScanSuccessRef.current(decodedText.trim());
    },
    [stopIdleTimer]
  );

  const detectFrame = useCallback(async () => {
    if (hasScannedRef.current || scanBlockedRef.current || detectingRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) return;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return;

    detectingRef.current = true;
    try {
      const detector = barcodeDetectorRef.current;
      if (detector) {
        try {
          const codes = await detector.detect(video);
          const value = codes.find((code) => code.rawValue)?.rawValue;
          if (value) {
            handleDecoded(value);
            return;
          }
        } catch {
          // Fall through to canvas + jsQR.
        }
      }

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(video, 0, 0, width, height);

      let value = decodeWithJsQR(ctx, width, height);
      if (!value) {
        // Front cams are often mirrored — try a flipped frame.
        ctx.save();
        ctx.translate(width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, width, height);
        ctx.restore();
        value = decodeWithJsQR(ctx, width, height);
      }

      if (value) handleDecoded(value);
    } finally {
      detectingRef.current = false;
    }
  }, [handleDecoded]);

  const startScanLoop = useCallback(
    (runId: number) => {
      const tick = () => {
        if (runId !== runIdRef.current) return;
        void detectFrame();
        scanLoopRef.current = window.setTimeout(tick, SCAN_INTERVAL_MS);
      };
      tick();
    },
    [detectFrame]
  );

  const startCamera = useCallback(async () => {
    if (!showScanner) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionDenied(true);
      setCameraPermission("denied");
      return;
    }

    const runId = ++runIdRef.current;
    stopStream();
    hasScannedRef.current = false;
    scanBlockedRef.current = false;
    setScanTimedOut(false);
    setRedLine(true);
    setPermissionDenied(false);
    setCameraPermission("requesting");

    try {
      barcodeDetectorRef.current = getBarcodeDetector();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      if (runId !== runIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      // Video mounts with the permission overlay; wait one frame if needed.
      let video = videoRef.current;
      if (!video) {
        await new Promise((resolve) =>
          requestAnimationFrame(() => resolve(null))
        );
        video = videoRef.current;
      }
      if (!video) {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        setPermissionDenied(true);
        setCameraPermission("denied");
        return;
      }

      video.srcObject = stream;
      await video.play();

      if (runId !== runIdRef.current) return;

      setCameraPermission("granted");
      startIdleTimer();
      startScanLoop(runId);
    } catch (error) {
      console.error("Camera access denied:", error);
      if (runId !== runIdRef.current) return;
      setPermissionDenied(true);
      setCameraPermission("denied");
    }
  }, [showScanner, startIdleTimer, startScanLoop, stopStream]);

  const startCameraRef = useRef(startCamera);
  const stopStreamRef = useRef(stopStream);
  startCameraRef.current = startCamera;
  stopStreamRef.current = stopStream;

  const resumeScanning = useCallback(() => {
    hasScannedRef.current = false;
    scanBlockedRef.current = false;
    setScanTimedOut(false);
    setRedLine(true);
    setScannerEpoch((value) => value + 1);
  }, []);

  const handleClickToReactivate = () => {
    setHasScannedError?.(false);
    resumeScanning();
  };

  useEffect(() => {
    if (!showScanner) {
      runIdRef.current += 1;
      stopStreamRef.current();
      return;
    }
    void startCameraRef.current();
    return () => {
      runIdRef.current += 1;
      stopStreamRef.current();
    };
  }, [showScanner, scannerEpoch]);

  useEffect(() => {
    if (restartTrigger < 1) return;
    resumeScanning();
  }, [restartTrigger, resumeScanning]);

  const showScanAgain = Boolean(hasScannedError || scanTimedOut);
  const showCameraView =
    cameraPermission === "granted" && !permissionDenied;

  useEffect(() => {
    scanBlockedRef.current = showScanAgain;
  }, [showScanAgain]);

  const showPermissionPrompt =
    cameraPermission === "checking" || cameraPermission === "requesting";
  const showDenied = permissionDenied || cameraPermission === "denied";

  return (
    <>
      <div className="position-relative">
        {/* Keep media nodes mounted whenever possible so getUserMedia can attach. */}
        {!showDenied && (
          <>
            <video
              ref={videoRef}
              className="qrScannerVideo"
              muted
              playsInline
              autoPlay
            />
            <canvas ref={canvasRef} className="qrScannerCanvas" aria-hidden />
          </>
        )}

        {showPermissionPrompt && (
          <div className="cameraPermissionPrompt">
            <p>
              {cameraPermission === "checking"
                ? "Checking camera permission…"
                : "Requesting camera access…"}
            </p>
          </div>
        )}

        {showDenied && (
          <div className="permissionDenied text-start">
            <p className="deniedTxt">
              Camera access was denied. Please enable camera permissions in your
              browser settings.
            </p>
            <p className="mb-2">For example:</p>
            <p className="mb-2">
              - In Chrome: Click the icon in the address bar → Site Settings →
              Allow Camera
            </p>
            <p className="mb-2">
              - In Safari: Preferences → Websites → Camera → Allow
            </p>
            <button
              type="button"
              className="cameraPermissionButton"
              onClick={() => {
                setPermissionDenied(false);
                setCameraPermission("checking");
                setScannerEpoch((value) => value + 1);
              }}
            >
              TRY AGAIN
            </button>
          </div>
        )}

        {showCameraView && redLine && !scanTimedOut && (
          <div className="scanLine" />
        )}
      </div>
      {showScanAgain && (
        <>
          <div className="overlay" />
          <button
            className="btnPrimaryScanAgian"
            onClick={handleClickToReactivate}
          >
            SCAN AGAIN
          </button>
        </>
      )}
    </>
  );
};

export default QRScanner;
